import React, { useCallback } from 'react';
import { Layout, Pressable, Text } from '../../../../components/core';
import { HubSurveyResultQuery } from '../../../../graphQL';
import { getStylesheet } from '../../../../styles';
import { supportAreaIconMap } from '../utils/supportAreaIconMap';

type SupportArea = HubSurveyResultQuery['hubSurveyResult']['supportAreas'][number];

type SupportAreaButtonProps = {
  /** The support area identifier (e.g., 'academicSupport', 'mentalHealth') */
  supportArea: SupportArea;
  /** Callback fired when the button is pressed */
  onPress: (supportArea: string) => void;
};

/**
 * Individual support area button with responsive layout.
 * Displays different layouts for mobile (horizontal) and web (vertical).
 */
export const SupportAreaButton = ({
  supportArea,
  onPress,
}: SupportAreaButtonProps): JSX.Element => {
  const supportAreaKey = supportArea.supportArea;
  // Get the appropriate icon component based on the support area
  const IconComponent = supportAreaIconMap[supportAreaKey]?.white;

  const handlePress = useCallback(() => {
    onPress(supportAreaKey);
  }, [onPress, supportAreaKey]);

  return (
    <Pressable
      {...styles.supportAreaButton}
      _hover={styles.supportAreaButtonHover}
      _pressed={styles.supportAreaButtonHover}
      testID={`button-support-area-${supportArea.supportArea}`}
      onPress={handlePress}
      aria-label={`View ${supportArea.title} recommendations`}
    >
      <Layout.Shift
        flip
        gap={4}
        alignMobile="center"
        alignNonMobile="flex-start"
        {...styles.buttonContent}
      >
        {IconComponent && <IconComponent size={8} aria-hidden />}
        <Text.para color="lightText" fontWeight="medium" flexShrink={1}>
          {supportArea.title} {'>'}
        </Text.para>
      </Layout.Shift>
    </Pressable>
  );
};

const styles = getStylesheet({
  supportAreaButton: {
    backgroundColor: 'secondary.900',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'transparent',
    padding: 4,
    // Responsive width: full on mobile, calculated on tablet+
    width: { base: '100%', sm: '32%' },
    minWidth: { base: 'auto', sm: '200px' },
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },

  supportAreaButtonHover: {
    backgroundColor: 'secondary.800',
    borderColor: 'secondary.400',
    borderWidth: 2,
  },

  buttonContent: {
    width: '100%',
  },
});
